import React, { DragEvent, useState, useCallback, useEffect, useRef } from "react";
import { ButtonsWrapper, RegisterButton } from "../../twStyles";
import { FiPaperclip, FiUploadCloud } from "react-icons/fi";
import SaveDraftButton from "../../../../components/SaveDraftButton";
import ImageBox from "../../../../components/ImageBox/";
import PhotoAiPromptModal from "../../../../components/PhotoAiPromptModal";
import PhotoAiPreviewCompareCard from "../../../../components/PhotoAiPreviewCompareCard";
import { usePhotoAiEdit } from "../../../../hooks/usePhotoAiEdit";
import {
  fetchPropertyDraft,
  publishProperty,
} from "../../../../services/propertyDraft";

import { useAuth } from "../../../../hooks/auth";
import api from "../../../../services/api";
import refreshToken from "../../../../utils/refreshToken";
import { useDropzone } from "react-dropzone";
import { useHistory } from "react-router-dom";
import loadFile from "../../../../utils/loadFile";
import { useToast } from "../../../../hooks/toast";
import asyncForEach from "../../../../utils/asyncForEach";
import {
  buildPhotoReorderApiBody,
  buildPhotoReorderPayload,
  getPhotoOrderBaseline,
  normalizePhotoOrder,
  reorderPhotosAfterDrop,
  sanitizePhotoReorderQueue,
  validatePhotoReorderQueue,
} from "../../../../utils/photoOrder";
import {
  findFirstInvalidNumericPhotoFile,
  invalidNumericPhotoFileMessage,
  sortFilesByNumericPhotoName,
} from "../../../../utils/photoFileName";
import {
  extractPropertyPhotoBase64,
  formatPropertyPhotoError,
  inspectPropertyPhotoFile,
  mapUploadedPropertyPhoto,
  getApiPhotoUploadReason,
  PHOTO_DROPZONE_OPTIONS,
  PHOTO_INVALID_TYPE_REASON,
  shouldIgnorePhotoDrop,
  tooManyPropertyPhotosError,
} from "../../../../utils/propertyPhotoUpload";

function cn(...parts: (string | undefined | false)[]) {
  return parts.filter(Boolean).join(" ");
}

interface IPhotos {
  id: number;
  order?: number;
  url_l: string;
  url_m: string;
  url_s: string;
}

interface IImageError {
  name: string;
  reason: string;
}

interface IProps {
  propertyID?: number;
  canPublish?: boolean;
  publishBlockers?: string[];
  initialPhotos?: IPhotos[];
  onPublishMetaChange?: (meta: {
    canPublish: boolean;
    publishBlockers: string[];
  }) => void;
}

function PhotosForm({
  propertyID,
  canPublish = false,
  publishBlockers = [],
  initialPhotos = [],
  onPublishMetaChange,
}: IProps) {
  const history = useHistory();
  const { access_token, dateToExpires } = useAuth();
  const { addToast } = useToast();

  const [loadingPhotosRequest, setLoadingPhotosRequest] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [localCanPublish, setLocalCanPublish] = useState(canPublish);
  const [localPublishBlockers, setLocalPublishBlockers] =
    useState<string[]>(publishBlockers);

  const [photos, setPhotos] = useState<IPhotos[]>(initialPhotos);
  const [dragId, setDragId] = useState<string>();
  const photoOrderBaselineRef = useRef<number[]>(
    getPhotoOrderBaseline(normalizePhotoOrder(initialPhotos))
  );
  const [imageError, setImageError] = useState<IImageError[]>([]);

  useEffect(() => {
    setPhotos((prev) => {
      const incomingIds = initialPhotos.map((photo) => photo.id).join(",");
      const currentIds = prev.map((photo) => photo.id).join(",");
      if (incomingIds === currentIds || prev.length > initialPhotos.length) {
        return prev;
      }

      photoOrderBaselineRef.current = getPhotoOrderBaseline(
        normalizePhotoOrder(initialPhotos)
      );
      return initialPhotos;
    });
  }, [initialPhotos]);

  useEffect(() => {
    setLocalCanPublish(canPublish);
    setLocalPublishBlockers(publishBlockers);
  }, [canPublish, publishBlockers]);

  const refreshPublishMeta = useCallback(async () => {
    if (!propertyID) return;

    try {
      const token = await refreshToken(dateToExpires, access_token);
      api.defaults.headers.authorization = `Bearer ${token ?? access_token}`;
      const result = await fetchPropertyDraft(api, propertyID);
      setLocalCanPublish(result.canPublish);
      setLocalPublishBlockers(result.publishBlockers);
      onPublishMetaChange?.({
        canPublish: result.canPublish,
        publishBlockers: result.publishBlockers,
      });
    } catch {
      // Keep current UI state when refresh fails silently after photo ops.
    }
  }, [propertyID, access_token, dateToExpires, onPublishMetaChange]);

  const handlePhotoApplied = useCallback(
    (newPhoto: Record<string, unknown>) => {
      setPhotos((prev) => [
        ...prev,
        {
          id: newPhoto.id as number,
          url_l: (newPhoto.photo as string) || (newPhoto.url_l as string),
          url_m: (newPhoto.photo as string) || (newPhoto.url_m as string),
          url_s: (newPhoto.photo as string) || (newPhoto.url_s as string),
        },
      ]);
      refreshPublishMeta();
    },
    [refreshPublishMeta]
  );

  const {
    aiPreviews,
    generatingAI,
    isPromptModalOpen,
    isPromptModalLoading,
    promptModalPhotoId,
    promptModalPhotoUrl,
    promptDraft,
    setPromptDraft,
    openCustomEditPrompt,
    closeCustomEditPrompt,
    cancelGeneration,
    submitCustomEdit,
    handleGenerateVirtualStaging,
    handleApplyAI,
    handleDiscardAI,
    handleTryAgainAI,
  } = usePhotoAiEdit({
    accessToken: access_token,
    dateToExpires,
    photos,
    onPhotoApplied: handlePhotoApplied,
  });

  const formattedPhotos = normalizePhotoOrder(photos);

  const persistPhotoOrderIfNeeded = useCallback(async () => {
    const orderedPhotos = normalizePhotoOrder(photos);
    const queue = buildPhotoReorderPayload(
      photoOrderBaselineRef.current,
      orderedPhotos
    );
    const sanitizedQueue = sanitizePhotoReorderQueue(
      queue,
      orderedPhotos.map((photo) => photo.id)
    );
    const validation = validatePhotoReorderQueue(
      sanitizedQueue,
      orderedPhotos.length
    );
    if (!validation.ok) return;

    const token = await refreshToken(dateToExpires, access_token);
    api.defaults.headers.authorization = `Bearer ${token ?? access_token}`;
    await api.put(`/api/adm/photos`, buildPhotoReorderApiBody(sanitizedQueue));
    photoOrderBaselineRef.current = getPhotoOrderBaseline(orderedPhotos);
  }, [access_token, dateToExpires, photos]);

  const performRemovePhoto = useCallback(
    async (id: number) => {
      setIsRemoving(true);
      try {
        const token = await refreshToken(dateToExpires, access_token);
        api.defaults.headers.authorization = `Bearer ${!!token ? token : access_token}`;

        await api.delete(`/api/adm/photos/${id}`);

        setPhotos((prev) => {
          const nextPhotos = normalizePhotoOrder(prev.filter((p) => p.id !== id));
          photoOrderBaselineRef.current = getPhotoOrderBaseline(nextPhotos);
          return nextPhotos;
        });
        addToast({ title: "Imagem removida!", type: "success" });
        await refreshPublishMeta();
      } catch (err: any) {
        console.error(err);
        addToast({ title: "Erro ao remover imagem", type: "error" });
      } finally {
        setIsRemoving(false);
      }
    },
    [access_token, dateToExpires, addToast, refreshPublishMeta]
  );

  const handleOnDrop = useCallback(async (acceptedFiles: File[]) => {
    if (shouldIgnorePhotoDrop(acceptedFiles)) {
      return;
    }

    const tooMany = tooManyPropertyPhotosError(
      formattedPhotos.length,
      acceptedFiles.length
    );
    if (tooMany) {
      setImageError([tooMany]);
      return;
    }

    const invalidFile = findFirstInvalidNumericPhotoFile(acceptedFiles);
    if (invalidFile) {
      setImageError([
        {
          name: invalidFile.name,
          reason: invalidNumericPhotoFileMessage(invalidFile.name),
        },
      ]);
      return;
    }

    const imagesWithError: IImageError[] = [];
    let nextOrder = formattedPhotos.length;
    setLoadingPhotosRequest(true);
    setImageError([]);

    try {
      const sort = sortFilesByNumericPhotoName(acceptedFiles);

      await asyncForEach(sort, async (file) => {
        try {
          const inspectionError = await inspectPropertyPhotoFile(file);
          if (inspectionError) {
            imagesWithError.push(inspectionError);
            return;
          }

          const imageBase64 = extractPropertyPhotoBase64(await loadFile(file));
          if (!imageBase64) {
            imagesWithError.push({
              name: file.name,
              reason: PHOTO_INVALID_TYPE_REASON,
            });
            return;
          }

          const token = await refreshToken(dateToExpires, access_token);
          api.defaults.headers.authorization = `Bearer ${token ?? access_token}`;

          nextOrder += 1;
          const response = await api.post(
            `/api/adm/properties/${propertyID}/photos`,
            {
              order: nextOrder,
              photo: imageBase64,
            }
          );

          if (!response.data.success) {
            imagesWithError.push({
              name: file.name,
              reason: getApiPhotoUploadReason(response.data),
            });
            return;
          }

          setPhotos((prevValue) => {
            const nextPhotos = normalizePhotoOrder([
              ...prevValue,
              mapUploadedPropertyPhoto(response.data.data),
            ]);
            photoOrderBaselineRef.current = getPhotoOrderBaseline(nextPhotos);
            return nextPhotos;
          });
        } catch (err: any) {
          if (err.response?.data?.errors) {
            Object.values(err.response.data.errors).forEach((messages) => {
              if (!Array.isArray(messages)) return;
              messages.forEach((error: string) =>
                addToast({ title: error, type: "error" })
              );
            });
          }
          imagesWithError.push({
            name: file.name,
            reason: getApiPhotoUploadReason(err),
          });
        }
      });

      setImageError(imagesWithError);
      await refreshPublishMeta();
    } finally {
      setLoadingPhotosRequest(false);
    }
  }, [
    access_token,
    addToast,
    dateToExpires,
    formattedPhotos.length,
    propertyID,
    refreshPublishMeta,
  ]);

  function hadleDragImageBox(ev: DragEvent<HTMLDivElement>) {
    setDragId(ev.currentTarget.id);
  }

  function handleDropImageBox(ev: DragEvent<HTMLDivElement>) {
    if (!dragId) return;

    const { photos: reorderedPhotos } = reorderPhotosAfterDrop(
      formattedPhotos,
      dragId,
      ev.currentTarget?.id ?? ""
    );

    setPhotos(reorderedPhotos);
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    ...PHOTO_DROPZONE_OPTIONS,
    disabled: loadingPhotosRequest,
    onDrop: handleOnDrop,
  });

  const dropRootProps = getRootProps();

  const hasPhotos = photos.length > 0;

  async function handlePublish() {
    if (isPublishing || !propertyID) return;

    if (localPublishBlockers.length > 0) {
      addToast({
        title: localPublishBlockers[0],
        type: "error",
      });
      return;
    }

    setIsPublishing(true);
    try {
      try {
        await persistPhotoOrderIfNeeded();
      } catch {
        addToast({
          title: "Não foi possível salvar a nova ordem das fotos",
          type: "error",
        });
        return;
      }
      const token = await refreshToken(dateToExpires, access_token);
      api.defaults.headers.authorization = `Bearer ${!!token ? token : access_token}`;

      await publishProperty(api, propertyID);

      addToast({ title: "Imóvel publicado com sucesso!", type: "success" });
      history.push(`/property/${propertyID}`);
    } catch (err: any) {
      addToast({
        title: err?.response?.data?.message ?? "Erro ao publicar o imóvel",
        type: "error",
      });
    } finally {
      setIsPublishing(false);
    }
  }

  async function handleSaveDraftAndExit() {
    try {
      await persistPhotoOrderIfNeeded();
      addToast({ title: "Rascunho salvo com sucesso!", type: "success" });
      history.push("/properties/1");
    } catch {
      addToast({
        title: "Não foi possível salvar a nova ordem das fotos",
        type: "error",
      });
    }
  }

  return (
    <form className="mb-[25%] mt-10 w-full rounded-xl border border-slate-200 bg-white p-5 shadow-sm sm:p-8">
      <p className="text-xs font-medium text-slate-500">
        * Após upload as fotos são salvas automaticamente
      </p>

      {localPublishBlockers.length > 0 && (
        <div className="mt-4 rounded-md border-l-4 border-amber-400 bg-amber-50 p-4">
          <h3 className="text-sm font-medium text-amber-800">
            Para publicar, corrija os seguintes itens:
          </h3>
          <ul className="mt-2 list-disc pl-5 text-sm text-amber-700">
            {localPublishBlockers.map((blocker) => (
              <li key={blocker}>{blocker}</li>
            ))}
          </ul>
        </div>
      )}

      <div
        {...dropRootProps}
        className={cn(
          "mt-4 flex min-h-[128px] cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-6 text-center transition",
          isDragActive
            ? "border-amber-400/70 bg-amber-50/60"
            : "border-slate-200 bg-slate-50/70 hover:border-slate-300 hover:bg-slate-100/80",
          dropRootProps.className ?? ""
        )}
      >
        <input {...getInputProps()} />
        {!loadingPhotosRequest && (
          <>
            {isDragActive ? (
              <span className="text-sm font-medium text-slate-600">
                Solte as imagens aqui
              </span>
            ) : (
              <span className="flex flex-col items-center gap-2 text-sm font-semibold text-slate-700 sm:flex-row">
                <FiPaperclip className="h-6 w-6 text-slate-500" aria-hidden />
                Adicionar fotos
              </span>
            )}
          </>
        )}
        {loadingPhotosRequest && (
          <span className="text-sm font-medium text-slate-600">Carregando…</span>
        )}
      </div>

      {imageError.length >= 1 &&
        imageError.map((image, idx) => (
          <p
            key={`${image.name}-${idx}`}
            className="mt-3 text-sm text-red-600"
          >
            {formatPropertyPhotoError(image)}
          </p>
        ))}

      <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {formattedPhotos.map((photo) => (
          <ImageBox
            key={photo.id}
            photo={photo}
            boxNumber={photo.id}
            handleDrag={hadleDragImageBox}
            handleDrop={handleDropImageBox}
            handleRemovePhoto={() => performRemovePhoto(photo.id)}
            onVirtualStaging={handleGenerateVirtualStaging}
            onCustomEdit={openCustomEditPrompt}
            isGeneratingAI={generatingAI[photo.id] || false}
            isLoading={isRemoving}
          />
        ))}
      </div>

      {aiPreviews.length > 0 && (
        <div className="mt-10 rounded-xl border border-slate-200 bg-slate-50/80 p-5 sm:p-6">
          <h3 className="text-base font-semibold tracking-tight text-slate-800">
            Pré-visualizações geradas por IA (pendentes)
          </h3>
          <p className="mt-1 text-sm text-slate-600">
            Escolha aplicar ou descartar cada preview.
          </p>

          <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-[repeat(auto-fit,minmax(300px,1fr))]">
            {aiPreviews.map((preview) => {
              const original = photos.find((p) => p.id === preview.photo_id);
              return (
                <PhotoAiPreviewCompareCard
                  key={preview.id}
                  preview={preview}
                  fallbackOriginalUrl={original?.url_m}
                  onApply={handleApplyAI}
                  onDiscard={handleDiscardAI}
                  onTryAgain={handleTryAgainAI}
                />
              );
            })}
          </div>
        </div>
      )}

      <PhotoAiPromptModal
        open={isPromptModalOpen}
        prompt={promptDraft}
        photoPreviewUrl={promptModalPhotoUrl}
        isLoading={isPromptModalLoading}
        onPromptChange={setPromptDraft}
        onClose={closeCustomEditPrompt}
        onSubmit={submitCustomEdit}
        onCancelProcessing={() => {
          if (promptModalPhotoId) cancelGeneration(promptModalPhotoId);
        }}
      />

      {!hasPhotos && (
        <p className="mt-6 text-center text-sm font-medium text-amber-600">
          Adicione pelo menos 1 foto antes de publicar
        </p>
      )}

      <ButtonsWrapper>
        <SaveDraftButton onClick={handleSaveDraftAndExit} />

        <RegisterButton
          type="button"
          disabled={!localCanPublish || isPublishing}
          className={
            !localCanPublish || isPublishing
              ? "!cursor-not-allowed !opacity-60"
              : ""
          }
          onClick={
            !localCanPublish || isPublishing ? undefined : handlePublish
          }
          title={
            !localCanPublish
              ? "Complete os itens pendentes para publicar"
              : undefined
          }
        >
          <FiUploadCloud size={16} className="mr-1.5" />
          <p>{isPublishing ? "Publicando..." : "Publicar Imóvel"}</p>
        </RegisterButton>
      </ButtonsWrapper>
    </form>
  );
}

export default PhotosForm;
